import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from 'components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from 'components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from 'components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { AlertTriangle, Download, FileText } from 'lucide-react';
import {
  getJob, isTerminalJob, putRates, getRateSetRates, uploadRateRevision, applyRateRevision,
  discardRateRevision, fetchDocumentBlob, saveBlob,
  ContractDetail, DocumentItem, RateItem, RateSet,
} from './api';

const fmt = (d?: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : null);
const revLabel = (s: RateSet) => (s.revision_number === 0 ? 'Original rates' : `Revision R${s.revision_number}`);
const STATUS_LABEL: Record<string, string> = {
  live: 'Live', draft: 'Draft', processing: 'Reading…', superseded: 'Superseded', discarded: 'Discarded',
};
const STATUS_PILL: Record<string, string> = {
  live: 'bg-green-50 text-green-700 ring-green-600/20',
  draft: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  processing: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  superseded: 'bg-gray-50 text-gray-600 ring-gray-500/10',
  discarded: 'bg-gray-50 text-gray-500 ring-gray-500/10',
};
const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_PILL[status] ?? STATUS_PILL.superseded}`}>
    {STATUS_LABEL[status] ?? status}
  </span>
);
const changeSummary = (s: RateSet) =>
  s.mode === 'changes' ? `${s.changed_count} changed, ${s.added_count} added` : `${s.rows_count} rows`;

// ---- Routes & Rates: the live rate card by default; past revisions read-only; a new
// rate revision is read into a draft, reviewed here, then applied ----
const ContractRatesTab: React.FC<{
  contractId: number;
  detail: ContractDetail;
  canUpload: boolean;   // contracts.upload: add a rate revision
  canEdit: boolean;     // contracts.edit: apply / discard one
  onChanged: () => Promise<unknown>;
  onViewDocument: (documentId: number) => void;
  flash: (m: string) => void;
}> = ({ contractId, detail, canUpload, canEdit, onChanged, onViewDocument, flash }) => {
  const [viewId, setViewId] = useState<number | null>(null);   // null = the default (live) card
  const shownId = viewId ?? detail.rate_set_id;
  const shown = detail.rate_sets.find((s) => s.id === shownId) ?? null;
  const isDefault = shownId === detail.rate_set_id;
  const [rows, setRows] = useState<RateItem[]>(detail.rates);
  const [error, setError] = useState<string | null>(null);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const draft = detail.draft_revision;

  // the rows of the card being viewed: the default one comes with the contract
  useEffect(() => {
    setError(null);
    if (isDefault || shownId == null) { setRows(detail.rates); return; }
    let cancelled = false;
    getRateSetRates(shownId)
      .then((r) => { if (!cancelled) setRows(r.rates); })
      .catch((e: any) => { if (!cancelled) setError(e?.message || 'Could not load these rates'); });
    return () => { cancelled = true; };
  }, [shownId, isDefault, detail]);

  // a revision being read: poll its job, then reload once it's ready for review
  const readingJob = draft?.status === 'processing' ? draft.job_id : null;
  useEffect(() => {
    if (!readingJob) return;
    const t = setInterval(async () => {
      try {
        if (isTerminalJob((await getJob(readingJob)).status)) { clearInterval(t); onChanged(); }
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [readingJob, onChanged]);

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setError(null);
    try { await fn(); flash(done); } catch (e: any) { setError(e?.message || 'Something went wrong'); }
  };
  const save = () => run(async () => {
    if (!shown) return;
    await putRates(shown.id, rows);
    await onChanged();
  }, 'Rates saved');
  const apply = (s: RateSet) => {
    if (!window.confirm(`Make ${revLabel(s)} the live rates? The current rates are kept as a past revision.`)) return;
    run(async () => { await applyRateRevision(s.id); setViewId(null); await onChanged(); }, `${revLabel(s)} is now live`);
  };
  const discard = (s: RateSet) => {
    if (!window.confirm(`Discard ${revLabel(s)}? The live rates stay as they are.`)) return;
    run(async () => { await discardRateRevision(s.id); setViewId(null); await onChanged(); }, `${revLabel(s)} discarded`);
  };

  // rate sheets of the card being viewed that couldn't be read
  const sheets = detail.documents.filter((d) => d.document_type === 'rate_sheet' && d.rate_set_id === shownId);
  const problems = sheets.filter((d) => d.parse_status === 'failed' || d.parse_status === 'no_rows');
  const mainDoc = detail.documents.find((d) => d.document_type === 'main');
  const nothingFromContract = shown?.revision_number === 0 && shown.status !== 'processing' && rows.length === 0
    && problems.length === 0 && detail.lifecycle_status !== 'processing';
  const isPast = !!shown && !isDefault && shown.id !== draft?.id;
  const reviewingDraft = !!draft && shown?.id === draft.id;
  const canRevise = canUpload && detail.lifecycle_status === 'active' && !draft;

  return (
    <div className="space-y-3">
      {/* which rate card, and the revision history */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {shown && (
            <>
              <span className="font-medium">{revLabel(shown)}</span>
              <StatusPill status={shown.status} />
              {shown.effective_from && <span className="text-muted-foreground">effective {fmt(shown.effective_from)}</span>}
              {shown.mode === 'changes' && <span className="text-muted-foreground">· changes only</span>}
              {shown.remarks && <span className="text-muted-foreground truncate max-w-xs" title={shown.remarks}>· {shown.remarks}</span>}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {detail.rate_sets.length > 1 && (
            <Select value={String(shownId ?? '')} onValueChange={(v) => setViewId(Number(v) === detail.rate_set_id ? null : Number(v))}>
              <SelectTrigger className="w-80"><SelectValue placeholder="Revision…" /></SelectTrigger>
              <SelectContent>
                {detail.rate_sets.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    {revLabel(s)} · {STATUS_LABEL[s.status] ?? s.status} · {fmt(s.effective_from) ?? fmt(s.applied_at ?? s.created_at)} · {s.rows_count} rows
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canRevise && <Button variant="outline" onClick={() => setRevisionOpen(true)}>Add rate revision</Button>}
        </div>
      </div>

      {isPast && (
        <div className="rounded bg-muted text-sm px-3 py-2 flex items-center justify-between gap-3">
          <span>Viewing past rates — read-only.</span>
          <button className="text-blue-700 hover:underline" onClick={() => setViewId(null)}>Back to the latest rates</button>
        </div>
      )}

      {/* the revision being read / waiting for review */}
      {draft && (
        <div className="rounded border border-amber-200 bg-amber-50 text-amber-900 text-sm px-3 py-2 flex flex-wrap items-center justify-between gap-3">
          {draft.status === 'processing' ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-amber-700" />
              Reading {revLabel(draft)} from its rate sheets… the live rates stay in use meanwhile.
            </span>
          ) : (
            <span>
              {revLabel(draft)}{draft.mode === 'changes' ? ' (changes only)' : ''}
              {draft.effective_from ? `, effective ${fmt(draft.effective_from)},` : ''}{' '}
              {draft.job_status === 'failed'
                ? <>could not be read{draft.job_error ? ` (${draft.job_error})` : ''} — add the rows by hand, or discard it and upload again.</>
                : <>is ready for review: {changeSummary(draft)}. Until it's applied, the live rates stay in use.</>}
            </span>
          )}
          {draft.status === 'draft' && (
            <div className="flex gap-2">
              {!reviewingDraft && <Button size="sm" variant="outline" onClick={() => setViewId(draft.id)}>Review</Button>}
              {canEdit && <Button size="sm" onClick={() => apply(draft)} disabled={draft.rows_count === 0}>Apply revision</Button>}
              {canEdit && <Button size="sm" variant="outline" onClick={() => discard(draft)}>Discard</Button>}
            </div>
          )}
        </div>
      )}

      {/* rate sheets that couldn't be tabulated: say so, and hand over the source */}
      {problems.length > 0 && (
        <div className="rounded border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2 space-y-1.5">
          {problems.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-start gap-2 min-w-0">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="min-w-0">
                  Couldn't read rates from <span className="font-medium">{d.file_name}</span>
                  {d.parse_status === 'no_rows' ? ': no rate rows found in it.' : d.parse_error ? ` (${d.parse_error}).` : '.'}
                  {' '}{shown?.editable ? 'Check the sheet and enter its rates below.'
                    : canRevise ? 'Check the sheet; to correct these rates, add a rate revision.' : 'Check the sheet.'}
                </span>
              </span>
              <SourceButtons contractId={contractId} doc={d} onView={onViewDocument} onError={setError} />
            </div>
          ))}
        </div>
      )}
      {nothingFromContract && (
        <div className="rounded border border-amber-200 bg-amber-50 text-amber-900 text-sm px-3 py-2 flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            No rates could be tabulated from the contract{sheets.length ? ' or its rate sheets' : ''}.
            {shown?.editable ? ' Check the source and enter them below.' : ' Check the source.'}
          </span>
          {mainDoc && <SourceButtons contractId={contractId} doc={mainDoc} onView={onViewDocument} onError={setError} />}
        </div>
      )}
      {error && <div className="rounded bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      <RatesEditor rates={rows} setRates={setRows} readOnly={!shown?.editable} onSave={save}
        highlightChanges={shown?.mode === 'changes'} />

      <RateRevisionDialog open={revisionOpen} onOpenChange={setRevisionOpen} contractId={contractId}
        onUploaded={async () => { setRevisionOpen(false); await onChanged(); flash('Rate revision uploaded — reading it now'); }} />
    </div>
  );
};

const SourceButtons: React.FC<{ contractId: number; doc: DocumentItem; onView: (id: number) => void; onError: (m: string) => void }> = ({ contractId, doc, onView, onError }) => (
  <span className="flex shrink-0 gap-2">
    <Button size="sm" variant="outline" className="bg-white" onClick={() => onView(doc.id)}>
      <FileText className="mr-1.5 h-3.5 w-3.5" />View
    </Button>
    <Button size="sm" variant="outline" className="bg-white"
      onClick={async () => {
        try { saveBlob(await fetchDocumentBlob(contractId, doc), doc.file_name); }
        catch (e: any) { onError(e?.message || 'Download failed'); }
      }}>
      <Download className="mr-1.5 h-3.5 w-3.5" />Download
    </Button>
  </span>
);

// ---- upload new rates for an active contract ----
const RateRevisionDialog: React.FC<{
  open: boolean; onOpenChange: (o: boolean) => void; contractId: number; onUploaded: () => Promise<void>;
}> = ({ open, onOpenChange, contractId, onUploaded }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [mode, setMode] = useState<'full' | 'changes'>('full');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) { setFiles([]); setMode('full'); setEffectiveFrom(''); setRemarks(''); setError(null); }
  }, [open]);

  const submit = async () => {
    if (files.length === 0) { setError('Add at least one rate sheet'); return; }
    const fd = new FormData();
    files.forEach((f) => fd.append('rate_sheets', f));
    fd.append('mode', mode);
    if (effectiveFrom) fd.append('effective_from', effectiveFrom);
    if (remarks.trim()) fd.append('remarks', remarks.trim());
    setSubmitting(true);
    setError(null);
    try {
      await uploadRateRevision(contractId, fd);
      await onUploaded();
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add rate revision</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Rate sheets *</Label>
            <Input type="file" multiple accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg"
              onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </div>
          <div className="space-y-1.5">
            <Label>These sheets are…</Label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="radio" name="rev-mode" className="mt-1" checked={mode === 'full'} onChange={() => setMode('full')} />
              <span>A full new rate card <span className="block text-xs text-muted-foreground">replaces all current routes and rates</span></span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="radio" name="rev-mode" className="mt-1" checked={mode === 'changes'} onChange={() => setMode('changes')} />
              <span>Changes only <span className="block text-xs text-muted-foreground">updates the routes listed (same origin, destination and vehicle) and adds new ones; every other route carries forward</span></span>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Effective from</Label>
              <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
            </div>
            <div>
              <Label>Remarks</Label>
              <Input value={remarks} maxLength={500} placeholder="e.g. Diesel hike, Nov 2026" onChange={(e) => setRemarks(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            The new rates are read into a draft for review; the current rates stay live until you apply it.
          </p>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? 'Uploading…' : 'Upload & read'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- the rate table ----
const MAX_DETAIL_COLUMNS = 6;

// all of a rate row's extra details, behind one button (used when there are many)
const RateDetails: React.FC<{ details: Record<string, string> }> = ({ details }) => {
  const entries = Object.entries(details);
  if (entries.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="mt-0.5 whitespace-nowrap">
          {entries.length} detail{entries.length === 1 ? '' : 's'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[28rem] max-h-96 overflow-y-auto">
        <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-1.5 text-sm">
          {entries.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="break-words">{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  );
};

type RateCol = {
  key: 'origin_text' | 'destination_text' | 'vehicle_type' | 'rate' | 'rate_unit' | 'minimum_charge' | 'effective_from' | 'effective_to';
  label: string;
  kind: 'text' | 'number' | 'date';
  width: string;
  always?: boolean;  // shown even when no row has a value
};
const RATE_COLS: RateCol[] = [
  { key: 'origin_text', label: 'Origin', kind: 'text', width: 'min-w-[160px]', always: true },
  { key: 'destination_text', label: 'Destination', kind: 'text', width: 'min-w-[160px]', always: true },
  { key: 'vehicle_type', label: 'Vehicle', kind: 'text', width: 'min-w-[140px]' },
  { key: 'rate', label: 'Rate', kind: 'number', width: 'min-w-[130px]', always: true },
  { key: 'rate_unit', label: 'Unit', kind: 'text', width: 'min-w-[110px]' },
  { key: 'minimum_charge', label: 'Min', kind: 'number', width: 'min-w-[110px]' },
  { key: 'effective_from', label: 'Valid from', kind: 'date', width: 'min-w-[150px]' },
  { key: 'effective_to', label: 'Valid to', kind: 'date', width: 'min-w-[150px]' },
];
// a changes-only revision marks what it changed against the live card
const CHANGE_ROW: Record<string, string> = { changed: 'bg-amber-50/70', added: 'bg-green-50/70', carried: 'opacity-60' };
const CHANGE_TAG: Record<string, string> = { changed: 'text-amber-800', added: 'text-green-700' };

const RatesEditor: React.FC<{
  rates: RateItem[]; setRates: (r: RateItem[]) => void; onSave: () => void; readOnly: boolean; highlightChanges?: boolean;
}> = ({ rates, setRates, onSave, readOnly, highlightChanges }) => {
  const [showAll, setShowAll] = useState(false);
  const upd = (i: number, patch: Partial<RateItem>) => setRates(rates.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  // optional columns only appear when some row has a value (or the user asks for them)
  const cols = RATE_COLS.filter((c) => c.always || showAll || rates.some((r) => r[c.key] != null && r[c.key] !== ''));
  const hidden = RATE_COLS.length - cols.length;
  // extra rate-sheet columns, in the order they first appear. A spreadsheet has a few
  // consistent ones (a column each); a quote / letter read from a PDF can carry a dozen,
  // which then go behind one "Details" button per row instead of widening the table.
  const detailKeys = Array.from(new Set(rates.flatMap((r) => Object.keys(r.details ?? {}))));
  const collapseDetails = detailKeys.length > MAX_DETAIL_COLUMNS;
  const detailCols = collapseDetails ? [] : detailKeys;
  const parse = (c: RateCol, v: string) => (v === '' ? null : c.kind === 'number' ? Number(v) : v);
  const width = cols.length + detailCols.length + (collapseDetails ? 1 : 0) + (highlightChanges ? 1 : 0);
  return (
    <div className="space-y-3">
      {(hidden > 0 || showAll) && (
        <button className="text-xs text-blue-600 hover:underline" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Hide empty columns' : `Show all columns (${hidden} empty hidden)`}
        </button>
      )}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            {highlightChanges && <TableHead className="text-muted-foreground">Change</TableHead>}
            {cols.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
            {detailCols.map((k) => <TableHead key={k} className="whitespace-nowrap text-muted-foreground">{k}</TableHead>)}
            {collapseDetails && <TableHead className="text-muted-foreground">Details</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {rates.length === 0 && <TableRow><TableCell colSpan={width} className="text-center text-muted-foreground py-4">No rate rows.</TableCell></TableRow>}
            {rates.map((r, i) => (
              <TableRow key={i}
                className={highlightChanges && r.change_kind ? CHANGE_ROW[r.change_kind] : undefined}>
                {highlightChanges && (
                  <TableCell className={`align-top pt-4 text-xs font-medium capitalize ${CHANGE_TAG[r.change_kind ?? ''] ?? 'text-muted-foreground'}`}>
                    {r.change_kind === 'carried' ? 'unchanged' : r.change_kind ?? 'new'}
                  </TableCell>
                )}
                {cols.map((c) => (
                  <TableCell key={c.key} className="align-top">
                    <Input className={c.width} disabled={readOnly} type={c.kind} value={r[c.key] ?? ''}
                      onChange={(e) => upd(i, { [c.key]: parse(c, e.target.value) })} />
                  </TableCell>
                ))}
                {detailCols.map((k) => <TableCell key={k} className="min-w-[110px] max-w-xs align-top text-sm text-muted-foreground">{r.details?.[k] ?? ''}</TableCell>)}
                {collapseDetails && <TableCell className="align-top"><RateDetails details={r.details ?? {}} /></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {!readOnly && <div className="flex gap-2">
        <Button variant="outline" onClick={() => setRates([...rates, { mapping_status: 'unmapped', change_kind: highlightChanges ? 'added' : null }])}>Add row</Button>
        <Button onClick={onSave}>Save rates</Button>
      </div>}
    </div>
  );
};

export default ContractRatesTab;
